/**
 * Shared print aspect deep-dive (Career / Wealth / Relationship).
 * Canva pages 7–9: editorial header, hero, pulse, star cards with cues, Do/Avoid.
 * Coach tip, related signal, and natal transforms are omitted so the sheet stays one A4.
 */

import React from "react";
import type { LucideIcon } from "lucide-react";
import { Briefcase, Sprout, Users } from "lucide-react";
import type {
  AspectLifePlaybook,
  DimensionScorecard,
  FocusStarSource,
  MonthlyStarSnapshot,
} from "../../../../utils/forecast/monthlyConsultation";
import { starCueForEnglishName } from "../../../../utils/forecast/monthlyConsultation/contentLibraries";
import { starToEnglish } from "../../../../utils/forecast/monthlyConsultation/englishLabels";
import heroCareer from "../../../../assets/monthly-consultation/pdf/hero-career.jpg";
import heroRelationship from "../../../../assets/monthly-consultation/pdf/hero-relationship.jpg";
import heroWealth from "../../../../assets/monthly-consultation/pdf/hero-wealth.jpg";
import {
  StarFamilyGlyph,
  starFamilyColor,
} from "../../shared/StarFamilyGlyph";
import { PdfChapterShell, PdfScoreBar } from "../atoms";
import { clipPdfText, stripPalaceSuffix, stripPrintDashes } from "../pdfCopy";
import {
  bodyFontFamily,
  border,
  cardWhite,
  charcoal,
  cream,
  displayFontFamily,
  green,
  mutedGray,
  paleGreen,
  palePink,
  tanGold,
  terracotta,
} from "../pdfTheme";

/**
 * Props for one aspect PDF chapter.
 */
export interface PdfAspectChapterProps {
  /** Chapter id for data-pdf-chapter. */
  chapterId: "career" | "wealth" | "relationships";
  /** Display title (Career / Wealth / Relationship). */
  title: string;
  /** Eyebrow label (unused — editorial header draws `+ TITLE`). */
  eyebrow: string;
  /** Aspect playbook with stars + do/avoid. */
  playbook: AspectLifePlaybook;
  /** Aspect palace snapshot (fallback labels). */
  snapshot: MonthlyStarSnapshot | null;
  /** Matching scorecard dimension for this life area (optional). */
  scorecard?: DimensionScorecard | null;
  /**
   * Page-break policy. Each life area starts on its own page by default.
   */
  breakBefore?: "always" | "auto";
  /** 1-based sequential page index among rendered sheets. */
  pageNumber?: number;
}

/** Maximum Do / Avoid lines shown on one A4 sheet. */
const ACTION_MAX = 3;
/** Soft cap for a Do / Avoid card sentence. */
const ACTION_CLIP = 88;
/** Soft cap for the pulse mechanism sentence. */
const MECHANISM_CLIP = 140;

/**
 * Lucide icon for an aspect chapter header tile.
 *
 * @param chapterId - Aspect chapter id
 * @returns Briefcase / Sprout / Users
 */
function aspectIcon(
  chapterId: PdfAspectChapterProps["chapterId"]
): LucideIcon {
  if (chapterId === "career") {
    return Briefcase;
  }
  if (chapterId === "wealth") {
    return Sprout;
  }
  return Users;
}

/**
 * Resolve the webpack URL for an aspect hero photo (Career, Wealth, or
 * Relationship). All three use user-supplied high-res JPEGs (Career
 * staircase, Wealth wooden cubes with baked-in **WEALTH** letters,
 * Relationship pinky-swear close-up).
 *
 * @param chapterId - Aspect chapter id
 * @returns Image URL, or empty string when the module did not yield a path
 */
function aspectHeroSrc(
  chapterId: PdfAspectChapterProps["chapterId"]
): string {
  let moduleSrc = "";
  if (chapterId === "career") {
    moduleSrc = typeof heroCareer === "string" ? heroCareer : "";
  } else if (chapterId === "wealth") {
    moduleSrc = typeof heroWealth === "string" ? heroWealth : "";
  } else {
    moduleSrc = typeof heroRelationship === "string" ? heroRelationship : "";
  }
  return moduleSrc.trim();
}

/**
 * Collect non-empty trimmed strings, capped at `limit`.
 *
 * @param value - Unknown array candidate
 * @param limit - Maximum rows
 * @returns Trimmed strings
 */
function stringRows(value: unknown, limit: number): string[] {
  if (!Array.isArray(value)) {
    return [];
  }
  const cap =
    typeof limit === "number" && Number.isFinite(limit) && limit > 0
      ? Math.trunc(limit)
      : ACTION_MAX;
  const out: string[] = [];
  for (const item of value) {
    if (typeof item !== "string") {
      continue;
    }
    const trimmed = item.trim();
    if (trimmed.length === 0) {
      continue;
    }
    out.push(clipPdfText(trimmed, ACTION_CLIP));
    if (out.length >= cap) {
      break;
    }
  }
  return out;
}

/**
 * Pad a 1-based index to a two-digit print marker (`01`).
 *
 * @param index - 1-based index
 * @returns Zero-padded marker
 */
function paddedMarker(index: number): string {
  const n =
    typeof index === "number" && Number.isFinite(index) && index > 0
      ? Math.trunc(index)
      : 1;
  return String(n).padStart(2, "0");
}

/**
 * English palace label from the aspect snapshot (chart name, else natal).
 *
 * @param snapshot - Aspect palace snapshot
 * @returns Trimmed palace name, or empty
 */
function snapshotPalaceLabel(snapshot: MonthlyStarSnapshot | null): string {
  if (snapshot === null) {
    return "";
  }
  const chart =
    typeof snapshot.chartPalaceNameEnglish === "string"
      ? snapshot.chartPalaceNameEnglish.trim()
      : "";
  if (chart.length > 0) {
    return chart;
  }
  return typeof snapshot.palaceNameEnglish === "string"
    ? snapshot.palaceNameEnglish.trim()
    : "";
}

/**
 * One-line open/borrowed palace note (Canva Relationship: "Spouse is open...").
 *
 * @param starSource - Resolved star source
 * @param snapshot - Aspect palace snapshot used when focus name is blank
 * @returns Note string, or empty when natal / unresolved
 */
function palaceOpenNote(
  starSource: FocusStarSource | null,
  snapshot: MonthlyStarSnapshot | null
): string {
  if (starSource === null) {
    return "";
  }
  if (starSource.mode === "natal") {
    return "";
  }
  const focusRaw =
    typeof starSource.focusPalaceNameEnglish === "string" &&
    starSource.focusPalaceNameEnglish.trim().length > 0
      ? starSource.focusPalaceNameEnglish
      : snapshotPalaceLabel(snapshot);
  const focus = stripPalaceSuffix(focusRaw);
  const fromRaw =
    typeof starSource.borrowedFromPalaceNameEnglish === "string"
      ? starSource.borrowedFromPalaceNameEnglish
      : "";
  const from = stripPalaceSuffix(fromRaw);
  if (starSource.mode === "open" && focus.length > 0 && from.length > 0) {
    return `${focus} is open, showing stars from opposite ${from}.`;
  }
  if (starSource.mode === "open" && focus.length > 0) {
    return `${focus} is open this month.`;
  }
  if (starSource.mode === "borrowed" && from.length > 0) {
    return `Showing stars from opposite ${from}.`;
  }
  return "";
}

/**
 * Star-card subtitle: `in Career` (natal) or `from Career` (borrowed/open).
 *
 * @param starSource - Resolved star source
 * @param aspectTitle - Chapter display title
 * @returns Placement label
 */
function starPlacementLabel(
  starSource: FocusStarSource,
  aspectTitle: string
): string {
  const fromRaw =
    typeof starSource.borrowedFromPalaceNameEnglish === "string"
      ? starSource.borrowedFromPalaceNameEnglish
      : "";
  const from = stripPalaceSuffix(fromRaw);
  if (
    (starSource.mode === "borrowed" || starSource.mode === "open") &&
    from.length > 0
  ) {
    return `from ${from}`;
  }
  return `in ${aspectTitle}`;
}

/**
 * Thin gold/terracotta rule with a diamond at the right end.
 *
 * @param props - Stroke color
 * @returns Rule row
 */
const PdfDiamondRule: React.FC<{ readonly color: string }> = ({ color }) => {
  const stroke =
    typeof color === "string" && color.trim().length > 0
      ? color.trim()
      : tanGold;
  return (
    <div
      aria-hidden="true"
      style={{
        display: "flex",
        alignItems: "center",
        width: "100%",
      }}
    >
      <span
        style={{
          flex: 1,
          height: 1,
          background: stroke,
        }}
      />
      <span
        style={{
          width: 6,
          height: 6,
          background: stroke,
          transform: "rotate(45deg)",
          flexShrink: 0,
          marginLeft: 1,
        }}
      />
    </div>
  );
};

/**
 * Column header: `DO` / `AVOID` plus a colored diamond rule.
 *
 * @param props - Label and accent
 * @returns Header row
 */
const PdfColumnRule: React.FC<{
  readonly label: string;
  readonly color: string;
}> = ({ label, color }) => {
  const safe = typeof label === "string" ? label.trim() : "";
  const stroke =
    typeof color === "string" && color.trim().length > 0
      ? color.trim()
      : tanGold;
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        marginBottom: 8,
        fontFamily: bodyFontFamily,
      }}
    >
      <span
        style={{
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: "0.16em",
          textTransform: "uppercase",
          color: stroke,
          flexShrink: 0,
        }}
      >
        {safe}
      </span>
      <PdfDiamondRule color={stroke} />
    </div>
  );
};

/**
 * Compact “This month's pulse” block restyled to Canva cream / terracotta / gold.
 * Always rendered. Score bar, Prior/Now/Next, and mechanism stay when present.
 *
 * @param props - Optional scorecard row
 * @returns Pulse section
 */
const AspectPulse: React.FC<{
  readonly score: DimensionScorecard | null;
}> = ({ score }) => {
  const mechanism =
    score !== null && typeof score.mechanism === "string"
      ? stripPrintDashes(clipPdfText(score.mechanism, MECHANISM_CLIP))
      : "";

  return (
    <div
      style={{
        padding: "4px 0 2px",
        borderBottom: `1px solid ${border}`,
        fontFamily: bodyFontFamily,
        flexShrink: 0,
      }}
    >
      <p
        style={{
          margin: "0 0 8px",
          fontFamily: displayFontFamily,
          fontSize: 14,
          fontWeight: 700,
          color: charcoal,
        }}
      >
        This month&apos;s pulse
      </p>
      {score !== null ? (
        <>
          <PdfScoreBar
            label={score.label}
            pct={score.pct}
            color={terracotta}
          />
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr 1fr",
              gap: 8,
              marginTop: 10,
            }}
          >
            {[
              {
                key: "prior",
                label: "Prior",
                value: `${String(Math.round(score.priorPct))}%`,
                emphasize: false,
              },
              {
                key: "now",
                label: "Now",
                value: `${String(Math.round(score.pct))}%`,
                emphasize: true,
              },
              {
                key: "next",
                label: "Next",
                value: `${String(Math.round(score.nextPct))}%`,
                emphasize: false,
              },
            ].map((cell) => (
              <div
                key={cell.key}
                style={{
                  background: cream,
                  border: `1px solid ${border}`,
                  borderTop: `2px solid ${cell.emphasize ? terracotta : tanGold}`,
                  borderRadius: 10,
                  padding: "6px 8px",
                  textAlign: "center",
                }}
              >
                <p
                  style={{
                    margin: 0,
                    fontSize: 9,
                    fontWeight: 700,
                    letterSpacing: "0.10em",
                    textTransform: "uppercase",
                    color: mutedGray,
                  }}
                >
                  {cell.label}
                </p>
                <p
                  style={{
                    margin: "3px 0 0",
                    fontFamily: displayFontFamily,
                    fontSize: 16,
                    fontWeight: 700,
                    color: cell.emphasize ? terracotta : charcoal,
                  }}
                >
                  {cell.value}
                </p>
              </div>
            ))}
          </div>
          {mechanism.length > 0 ? (
            <p
              style={{
                margin: "10px 0 0",
                fontSize: 12,
                lineHeight: 1.45,
                color: mutedGray,
              }}
            >
              {mechanism}
            </p>
          ) : null}
        </>
      ) : (
        <p
          style={{
            margin: 0,
            fontSize: 12,
            lineHeight: 1.45,
            color: mutedGray,
          }}
        >
          Score for this area is not available this month.
        </p>
      )}
    </div>
  );
};

/**
 * Resolve a print safe star definition from the shared STAR_CUES library.
 *
 * @param englishStar - Romanized star name
 * @returns Cue without dash punctuation, or a short fallback
 */
function aspectStarDefinition(englishStar: string): string {
  const cue = starCueForEnglishName(englishStar);
  if (cue.length > 0) {
    return stripPrintDashes(clipPdfText(cue, 120));
  }
  return "Notice how this energy shows up in decisions this month.";
}

/**
 * Two side by side star cards with name, placement, and cue definition.
 *
 * @param props - Star source and aspect title
 * @returns Stars block
 */
const AspectStars: React.FC<{
  readonly starSource: FocusStarSource | null;
  readonly snapshot: MonthlyStarSnapshot | null;
  readonly aspectTitle: string;
}> = ({ starSource, snapshot, aspectTitle }) => {
  const note = palaceOpenNote(starSource, snapshot);
  const stars =
    starSource !== null && Array.isArray(starSource.displayMainStars)
      ? starSource.displayMainStars.slice(0, 2)
      : [];
  const placement =
    starSource !== null ? starPlacementLabel(starSource, aspectTitle) : "";

  return (
    <div style={{ flexShrink: 0 }}>
      <p
        style={{
          margin: "0 0 8px",
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: "0.16em",
          textTransform: "uppercase",
          color: tanGold,
          fontFamily: bodyFontFamily,
        }}
      >
        Stars in this palace
      </p>
      {note.length > 0 ? (
        <p
          style={{
            margin: "0 0 8px",
            fontSize: 11,
            lineHeight: 1.45,
            color: mutedGray,
            fontFamily: bodyFontFamily,
          }}
        >
          {stripPrintDashes(note)}
        </p>
      ) : null}
      {starSource === null ? (
        <div
          style={{
            border: `1px dashed ${border}`,
            borderRadius: 12,
            padding: "10px 12px",
            fontFamily: bodyFontFamily,
            fontSize: 12,
            color: mutedGray,
          }}
        >
          Aspect palace could not be resolved for this month.
        </div>
      ) : stars.length === 0 ? (
        <div
          style={{
            border: `1px dashed ${border}`,
            borderRadius: 12,
            padding: "10px 12px",
            fontFamily: bodyFontFamily,
            fontSize: 12,
            color: mutedGray,
            lineHeight: 1.45,
          }}
        >
          Open palace this month with no main stars sitting here. Lean on Do and
          Avoid below.
        </div>
      ) : (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 10,
          }}
        >
          {stars.map((star) => {
            const english = starToEnglish(star);
            const color = starFamilyColor(english);
            const definition = aspectStarDefinition(english);
            return (
              <div
                key={star}
                className="mc-pdf-card"
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 8,
                  background: cardWhite,
                  border: `1px solid ${border}`,
                  borderLeft: `3px solid ${color}`,
                  borderRadius: 14,
                  padding: "10px 12px",
                  fontFamily: bodyFontFamily,
                  boxSizing: "border-box",
                }}
              >
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <span
                    style={{
                      width: 30,
                      height: 30,
                      borderRadius: "50%",
                      background: `${color}18`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                    aria-hidden="true"
                  >
                    <StarFamilyGlyph starName={english} size={14} />
                  </span>
                  <div style={{ minWidth: 0 }}>
                    <p
                      style={{
                        margin: 0,
                        fontFamily: displayFontFamily,
                        fontSize: 15,
                        fontWeight: 700,
                        color: charcoal,
                        lineHeight: 1.2,
                      }}
                    >
                      {english}
                    </p>
                    <p
                      style={{
                        margin: "2px 0 0",
                        fontSize: 10,
                        color: mutedGray,
                      }}
                    >
                      {placement}
                    </p>
                  </div>
                </div>
                <p
                  style={{
                    margin: 0,
                    fontSize: 11,
                    lineHeight: 1.45,
                    color: mutedGray,
                  }}
                >
                  {definition}
                </p>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

/**
 * Decorative cream icon band used only when an aspect hero src is empty.
 *
 * @param props - Lucide icon for the band
 * @returns Rounded cream placeholder
 */
const AspectIconBand: React.FC<{ readonly Icon: LucideIcon }> = ({ Icon }) => {
  return (
    <div
      aria-hidden="true"
      style={{
        height: 72,
        borderRadius: 14,
        background: cream,
        border: `1px solid ${border}`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        marginBottom: 12,
        boxSizing: "border-box",
      }}
    >
      <span
        style={{
          width: 40,
          height: 40,
          borderRadius: 12,
          background: `${tanGold}22`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Icon size={20} color={terracotta} strokeWidth={1.8} />
      </span>
    </div>
  );
};

/**
 * Print-native Do / Avoid tinted cards (not the web ActionTileList).
 *
 * @param props - Tone, lines, and empty fallback
 * @returns One column of cards
 */
const AspectActionColumn: React.FC<{
  readonly tone: "do" | "avoid";
  readonly lines: readonly string[];
  readonly emptyFallback: string;
}> = ({ tone, lines, emptyFallback }) => {
  const isDo = tone === "do";
  const accent = isDo ? green : terracotta;
  const tint = isDo ? paleGreen : palePink;
  const rows = lines.length > 0 ? lines : [emptyFallback];

  return (
    <div style={{ display: "flex", flexDirection: "column", minHeight: 0 }}>
      <PdfColumnRule label={isDo ? "Do" : "Avoid"} color={accent} />
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 10,
        }}
      >
        {rows.map((line, index) => (
          <div
            key={`${tone}-${String(index)}`}
            className="mc-pdf-card"
            style={{
              display: "flex",
              gap: 10,
              alignItems: "flex-start",
              background: tint,
              border: `1px solid ${accent}22`,
              borderRadius: 14,
              padding: "12px 14px",
              fontFamily: bodyFontFamily,
              boxSizing: "border-box",
            }}
          >
            <span
              aria-hidden="true"
              style={{
                width: 26,
                height: 26,
                borderRadius: "50%",
                background: cardWhite,
                color: accent,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: "0.04em",
                lineHeight: 1,
                marginTop: 1,
              }}
            >
              {isDo ? paddedMarker(index + 1) : "!"}
            </span>
            <p
              style={{
                margin: 0,
                fontSize: 13,
                lineHeight: 1.45,
                color: charcoal,
                fontWeight: 500,
              }}
            >
              {stripPrintDashes(line)}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
};

/**
 * Aspect deep-dive shell used by Career / Wealth / Relationships PDF chapters.
 * Section order: header → hero → pulse → stars → Do/Avoid.
 */
export const PdfAspectDeepDive: React.FC<PdfAspectChapterProps> = ({
  chapterId,
  title,
  playbook,
  snapshot,
  scorecard = null,
  breakBefore = "always",
  pageNumber,
}) => {
  const Icon = aspectIcon(chapterId);
  const safeTitle = typeof title === "string" ? title.trim() : "";
  const plusLabel =
    safeTitle.length > 0 ? `+ ${safeTitle.toUpperCase()}` : "";

  const doThis = stringRows(playbook.doThis, ACTION_MAX);
  const watchOut = stringRows(playbook.watchOut, ACTION_MAX);

  const score =
    scorecard !== null && typeof scorecard.pct === "number"
      ? scorecard
      : null;

  const hero = aspectHeroSrc(chapterId);
  const showPhotoHero = hero.length > 0;

  const starSource =
    playbook.starSource !== null && playbook.starSource !== undefined
      ? playbook.starSource
      : null;

  return (
    <PdfChapterShell
      chapterId={chapterId}
      eyebrow=""
      title=""
      hideHeader={true}
      breakBefore={breakBefore}
      pageNumber={pageNumber}
      compact
      fillPage={true}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 18,
          flex: "1 1 auto",
          minHeight: 0,
          height: "100%",
          fontFamily: bodyFontFamily,
          justifyContent: "space-between",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            flexShrink: 0,
          }}
        >
          <span
            aria-hidden="true"
            style={{
              width: 36,
              height: 36,
              borderRadius: 10,
              background: `${tanGold}22`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <Icon size={18} color={terracotta} strokeWidth={1.8} />
          </span>
          <div style={{ minWidth: 0 }}>
            {plusLabel.length > 0 ? (
              <p
                style={{
                  margin: 0,
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: "0.16em",
                  textTransform: "uppercase",
                  color: terracotta,
                }}
              >
                {plusLabel}
              </p>
            ) : null}
            <h2
              style={{
                margin: "2px 0 0",
                fontFamily: displayFontFamily,
                fontSize: 24,
                fontWeight: 700,
                lineHeight: 1.15,
                letterSpacing: "-0.02em",
                color: charcoal,
              }}
            >
              {safeTitle}
            </h2>
          </div>
        </div>

        <div style={{ flexShrink: 0 }}>
          <PdfDiamondRule color={tanGold} />
        </div>

        {showPhotoHero ? (
          <img
            src={hero}
            alt=""
            className={[
              "mc-pdf-hero-img",
              "mc-pdf-hero-img--aspect",
              chapterId === "career" ? "mc-pdf-hero-img--career" : "",
              chapterId === "wealth" ? "mc-pdf-hero-img--wealth" : "",
              chapterId === "relationships"
                ? "mc-pdf-hero-img--relationships"
                : "",
            ]
              .filter((token) => token.length > 0)
              .join(" ")}
            style={{ flexShrink: 0 }}
          />
        ) : (
          <AspectIconBand Icon={Icon} />
        )}

        <AspectPulse score={score} />

        <AspectStars
          starSource={starSource}
          snapshot={snapshot}
          aspectTitle={safeTitle}
        />

        <div
          className="mc-pdf-aspect-actions"
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 14,
            flex: "0 0 auto",
            alignItems: "start",
          }}
        >
          <AspectActionColumn
            tone="do"
            lines={doThis}
            emptyFallback="Keep one clear move in this area this month."
          />
          <AspectActionColumn
            tone="avoid"
            lines={watchOut}
            emptyFallback="Do not add a second front until the first is closed."
          />
        </div>
      </div>
    </PdfChapterShell>
  );
};
